'use client';
import { useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Typography from '@mui/material/Typography';
import useProducts from '@hooks/product/useProducts';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import BrandDivider from '@components/BrandDivider';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { GrillRecommendation } from '@repo/ui/src/types';
import ProductModel from '@models/product';
import { MediaType } from '@repo/ui/src/types/image';
import { Anchor } from '@repo/ui//src/components/Anchor';

// Types
interface Feature {
  title?: string;
  text?: string;
  tooltip?: string;
}
interface Answer {
  weight?: number;
  importance?: 'Very Important' | 'Default' | 'Less Important';
  recommendations: Array<{ id: string }>;
}

interface Question {
  answers?: Array<Answer>;
}

// Uses Product class from @models/product instead of this interface

interface Recommendation {
  product: ProductModel;
  images: Array<MediaType>;
  features?: Array<Feature>;
  showBundlesLink?: boolean;
  points?: number;
  sortOrder?: number;
}

interface QueryAnswer {
  question: number;
  answer: number;
}

interface LinkObject {
  href: string;
  text: string;
}

interface GrillRecommendationProps {
  addToCart?: string;
  alsoConsiderDescription?: string;
  alsoConsiderTitle?: string;
  compareGrills?: {
    link?: {
      link?: any;
    };
  };
  editAnswers?: string;
  exploreBundles?: {
    link?: {
      link?: any;
    };
  };
  featuresHeading: string;
  grillRecommendation: GrillRecommendation;
  loadingText?: string;
  outOfStockText?: string;
  recommendationTitle?: string;
  recommendations?: {
    recommendations?: Array<Recommendation>;
  };
  viewFullDetails: string;
}

const Recommendation = dynamic<any>(() => import('./_Recommendation'), { ssr: false });

const weights = {
  'Very Important': 10,
  Default: 3,
  'Less Important': 1,
};

const getAnswersFromRoute = (query: any): Array<QueryAnswer> => {
  if (!query?.entries) {
    return [];
  }
  return [...query.entries()]
    .filter((entry: [string, string]) => entry[0].includes('q') && !!entry[1])
    .reduce((total: Array<QueryAnswer>, next: [string, string]) => {
      return [...total, { question: Number(next[0].substring(1)), answer: Number(next[1]) }];
    }, []);
};

const computeResults = (
  questions: Array<Question>,
  answers: Array<QueryAnswer>,
  recommendations: Array<Recommendation>,
): Array<Recommendation> => {
  if (!questions?.length || !recommendations.length) return [];

  const results = [
    // Set initial points to 0 and set sort order based on placement in the list
    ...recommendations.map((x, i) => ({ ...x, points: 0, sortOrder: recommendations.length - i })),
  ];

  let i: number;
  let answer: QueryAnswer;
  let q: Question;
  let a: Answer;
  let j: number;
  let matchingResult: Recommendation | undefined;

  for (i = 0; i < answers.length; i++) {
    // For each user answer
    answer = answers[i];

    if (answer.question >= questions.length) continue;

    q = questions[answer.question]; // Find matching question

    if (answer.answer >= (q.answers?.length || 0)) continue;

    a = q.answers![answer.answer]; // Find the matching answer

    for (j = 0; j < a.recommendations.length; j++) {
      // For each recommendation, find the matching result
      matchingResult = results.find((x) => x?.product?.deliveryId === a.recommendations[j].id);
      if (!matchingResult) continue;

      // Weight by the given value and add that value to the recommendation's points
      matchingResult.points =
        (matchingResult.points || 0) + (a.weight || weights[a.importance || 'Default']);
    }
  }

  // Sort by points then by sort order
  results.sort(
    (a, b) => (b.points || 0) - (a.points || 0) || (b.sortOrder || 0) - (a.sortOrder || 0),
  );

  return results;
};

const GrillRecommendationResult: React.FC<GrillRecommendationProps> = ({
  addToCart,
  alsoConsiderDescription,
  alsoConsiderTitle,
  compareGrills,
  editAnswers,
  exploreBundles,
  featuresHeading,
  grillRecommendation,
  loadingText,
  outOfStockText,
  recommendationTitle,
  recommendations: recommendationList,
  viewFullDetails,
}) => {
  const { query } = useCurrentUrl();
  const numResultsToShow = 3;
  const { push } = useModal(ModalID.grillRecommendation);

  const initialAnswers = getAnswersFromRoute(query);
  const quizTitle = grillRecommendation?.quizTitle;

  const { products, isLoading } = useProducts(
    recommendationList?.recommendations?.map((r) => r.product) || [],
  ) as { products: Array<ProductModel>; isLoading: boolean };

  const recommendations = useMemo(
    () =>
      recommendationList?.recommendations?.map((r) => {
        const product = products.find((p) => p.sku === r.product.productData?.sku);
        return { ...r, product: product || r.product };
      }) || [],
    [recommendationList?.recommendations, products],
  );

  const results = useMemo(
    () => computeResults(grillRecommendation.questions, initialAnswers, recommendations),
    [initialAnswers, grillRecommendation.questions, recommendations],
  );

  const compareGrillsLinkObj = useMemo<LinkObject | null>(() => {
    const link = compareGrills?.link?.link ? buildLinkObj(compareGrills.link) : null;
    if (!link || !results?.length) return link;

    const params = results
      .slice(0, 3)
      .map((x) => `sku=${x.product?.productData?.sku}`)
      .join('&');
    const href = link.href.split('?')[0];
    return {
      ...link,
      href: `${href}?${params}`,
    };
  }, [compareGrills?.link, results]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.affirm?.ui?.refresh) {
      window.affirm.ui.refresh();
    }
  }, [results]);

  if (!results?.length) return null;

  return (
    <div className="tw-py-2 md:tw-py-4 dark-mode">
      <div className="tw-p-6 md:tw-py-5 tw-text-center tw-items-center tw-flex tw-flex-col tw-max-w-[1200px] tw-mx-auto">
        {recommendationTitle && (
          <>
            <Typography component="h1" variant="h3" align="center">
              {recommendationTitle}
            </Typography>
            <BrandDivider />
          </>
        )}
        {editAnswers && (
          <Button
            onClick={() =>
              push({
                grillRecommendation: {
                  ...grillRecommendation,
                  quizTitle: quizTitle,
                },
                answers: initialAnswers,
              })
            }
            variant={ButtonVariant.bare}
          >
            {editAnswers}
          </Button>
        )}
        <Recommendation
          recommendation={results[0]}
          content={{
            addToCart,
            featuresHeading,
            exploreBundles,
            viewFullDetails,
            loadingText,
            outOfStockText,
          }}
          loading={isLoading}
        />
        {results.length > 1 && (
          <div className="tw-mt-5">
            {alsoConsiderTitle && (
              <>
                <Typography component="h2" variant="h4">
                  {alsoConsiderTitle}
                </Typography>
                <BrandDivider />
              </>
            )}
            {alsoConsiderDescription && (
              <Typography variant="body1" className="tw-m-0 tw-mx-auto tw-mb-4 md:tw-max-w-[60%]">
                {alsoConsiderDescription}
              </Typography>
            )}
            {compareGrillsLinkObj && (
              <Button
                as={Anchor}
                link={compareGrillsLinkObj.href}
                variant={ButtonVariant.skewer}
                dark
              >
                {compareGrillsLinkObj.text}
              </Button>
            )}
            {results.slice(1, numResultsToShow).map((result, i) => (
              <Recommendation
                key={result?.product?.sku || i}
                recommendation={result}
                content={{
                  addToCart,
                  featuresHeading,
                  exploreBundles,
                  viewFullDetails,
                  loadingText,
                  outOfStockText,
                }}
                textLeft={i % 2 === 0}
                loading={isLoading}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrillRecommendationResult;
